import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { DollarSign, TrendingUp, Users, FileText, BarChart3 } from 'lucide-react';
import api from '../../lib/api';

export default function AdminFinance() {
  const [data, setData] = useState(null);
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [fRes, cRes] = await Promise.all([
        api.get('/api/finance/overview'),
        api.get('/api/finance/cost-centers')
      ]);
      setData(fRes.data);
      setCosts(cRes.data.cost_centers || []);
    } catch {} finally { setLoading(false); }
  };

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}</div>;

  const sub = data?.subscription || {};
  const ad = data?.advertising || {};

  return (
    <div className="space-y-6" data-testid="admin-finance-page">
      <div><h1 className="text-2xl font-bold flex items-center gap-2"><DollarSign className="w-6 h-6 text-[#B89B5E]" /> Finance Overview</h1><p className="text-sm text-[#5B616E] mt-1">Revenue, subscriptions, and cost management</p></div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="bg-white border-[#E7E0D6]"><CardContent className="pt-5"><div className="flex items-center gap-2 text-xs text-[#5B616E] mb-2"><DollarSign className="w-4 h-4 text-[#1F7A5A]" /> MRR</div><p className="text-2xl font-bold">${sub.mrr?.toLocaleString()}</p></CardContent></Card>
        <Card className="bg-white border-[#E7E0D6]"><CardContent className="pt-5"><div className="flex items-center gap-2 text-xs text-[#5B616E] mb-2"><TrendingUp className="w-4 h-4 text-[#1E5A8A]" /> ARR</div><p className="text-2xl font-bold">${sub.arr?.toLocaleString()}</p></CardContent></Card>
        <Card className="bg-white border-[#E7E0D6]"><CardContent className="pt-5"><div className="flex items-center gap-2 text-xs text-[#5B616E] mb-2"><Users className="w-4 h-4 text-[#B89B5E]" /> Subscribers</div><p className="text-2xl font-bold">{sub.total_subscribers}</p></CardContent></Card>
        <Card className="bg-white border-[#E7E0D6]"><CardContent className="pt-5"><div className="flex items-center gap-2 text-xs text-[#5B616E] mb-2"><BarChart3 className="w-4 h-4 text-[#4A1D2A]" /> Ad Impressions</div><p className="text-2xl font-bold">{(ad.total_impressions || 0).toLocaleString()}</p></CardContent></Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="bg-white border-[#E7E0D6]">
          <CardHeader><CardTitle className="text-sm">Subscribers by Tier</CardTitle></CardHeader>
          <CardContent>
            {Object.entries(sub.by_tier || {}).map(([tier, count]) => (
              <div key={tier} className="flex justify-between py-2 border-b border-[#E7E0D6] last:border-0 text-sm"><span className="capitalize">{tier}</span><span className="font-medium">{count}</span></div>
            ))}
            {Object.keys(sub.by_tier || {}).length === 0 && <p className="text-sm text-[#5B616E] text-center py-4">No subscribers yet</p>}
          </CardContent>
        </Card>

        <Card className="bg-white border-[#E7E0D6]">
          <CardHeader><CardTitle className="text-sm">Cost Centers</CardTitle></CardHeader>
          <CardContent>
            {costs.map(c => (
              <div key={c.name} className="flex justify-between items-center py-2 border-b border-[#E7E0D6] last:border-0">
                <span className="text-sm">{c.name}</span>
                <div className="text-right"><p className="text-sm font-medium">${c.spent?.toLocaleString()} <span className="text-[#5B616E] font-normal">/ ${c.budget?.toLocaleString()}</span></p>
                  <div className="w-24 h-1.5 bg-[#E7E0D6] rounded-full mt-1"><div className="h-full rounded-full" style={{ width: `${Math.min(c.spent/c.budget*100, 100)}%`, background: c.spent/c.budget > 0.9 ? '#B3261E' : '#1F7A5A' }} /></div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
